/**
 * Navigation Commands
 *
 * Commands for navigating the app: sidebar, terminal, explorer, scripts, home, tab history.
 */

import {
  Home, PanelLeft, Terminal, FolderTree, FileCode2, History, Search,
} from 'lucide-react';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useWorkspaceStore } from '../../workspace';
import { useSidebarStore } from '../../database-objects/store/sidebarStore';
import { useTerminalStore } from '../../terminal/store/terminalStore';
import { useTabHistoryStore } from '../../tab-history/store/tabHistoryStore';
import { isBrowserMode } from '../../browser-mode';

/** Close palette and run after a tick */
function closeAndRun(fn: () => void) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

export const navigationCommands: CommandDefinition[] = [
  {
    id: 'nav:go-to-home',
    label: 'Go to Home',
    category: 'navigation',
    icon: Home,
    keybindingId: 'global.startPage',
    keywords: ['start', 'home', 'welcome'],
    execute: () => closeAndRun(() => {
      useWorkspaceStore.getState().ensureStartPage();
    }),
  },
  {
    id: 'nav:toggle-sidebar',
    label: 'Toggle Sidebar',
    category: 'navigation',
    icon: PanelLeft,
    keybindingId: 'global.toggleSidebar',
    keywords: ['sidebar', 'panel', 'explorer'],
    execute: () => closeAndRun(() => {
      useSidebarStore.getState().toggleCollapsed();
    }),
  },
  {
    id: 'nav:toggle-terminal',
    label: 'Toggle Terminal',
    category: 'navigation',
    icon: Terminal,
    keybindingId: 'global.toggleTerminal',
    keywords: ['terminal', 'console', 'shell'],
    isVisible: () => !isBrowserMode(),
    execute: () => closeAndRun(() => {
      useTerminalStore.getState().togglePanel();
    }),
  },
  {
    id: 'nav:expand-explorer',
    label: 'Expand Explorer',
    category: 'navigation',
    icon: FolderTree,
    keybindingId: 'global.expandExplorer',
    keywords: ['explorer', 'database', 'objects', 'tree'],
    execute: () => closeAndRun(() => {
      const store = useSidebarStore.getState();
      if (store.isCollapsed) store.expand();
      if (store.explorerCollapsed) store.toggleExplorerCollapsed();
    }),
  },
  {
    id: 'nav:expand-scripts',
    label: 'Expand Scripts',
    category: 'navigation',
    icon: FileCode2,
    keybindingId: 'global.expandScripts',
    keywords: ['scripts', 'files', 'sql'],
    execute: () => closeAndRun(() => {
      const store = useSidebarStore.getState();
      if (store.isCollapsed) store.expand();
      if (store.scriptsCollapsed) store.toggleScriptsCollapsed();
    }),
  },
  {
    id: 'nav:tab-history',
    label: 'Tab History',
    category: 'navigation',
    icon: History,
    keywords: ['closed', 'tabs', 'recent', 'reopen'],
    execute: () => closeAndRun(() => {
      useTabHistoryStore.getState().openDialog();
    }),
  },
  {
    id: 'nav:search-query-history',
    label: 'Search Query History',
    category: 'navigation',
    icon: Search,
    keybindingId: 'global.searchQueryHistory',
    keywords: ['history', 'query', 'search', 'past', 'executed', 'recent'],
    execute: () => {
      useCommandPaletteStore.getState().openPage('query-history');
    },
  },
];
